/* Scroll reading text — <mw-scroll-reading> for sections/scroll-reading-text.liquid.

   Writes one number while the section is on screen: --sr-p (reveal, 0 → 1). CSS turns it into
   per-letter opacity (and the button's), so there is no per-letter JS and only one rect read
   per animation frame. The reveal runs over the first data-reveal-end share of the pinned
   travel (1 at the default speed: the whole stage, like Helix).

   Falls back to the full text in normal flow (data-static) when the text does not fit the
   pinned stage, under reduced motion, and while the section is selected in the theme editor. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const clamp = (value) => Math.min(Math.max(value, 0), 1);

class MwScrollReading extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-stage]');
    this.box = this.querySelector('[data-box]');
    if (!this.stage || !this.box) return;

    this.frame = 0;
    this.inView = false;
    this.editing = false;
    this.abort = new AbortController();
    const options = { passive: true, signal: this.abort.signal };

    this.request = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
    };
    window.addEventListener('scroll', this.request, options);
    window.addEventListener('resize', () => this.measure(), options);
    reducedMotion.addEventListener('change', () => this.setup(), { signal: this.abort.signal });
    document.addEventListener('shopify:section:select', (event) => this.onEditor(event, true), { signal: this.abort.signal });
    document.addEventListener('shopify:section:deselect', (event) => this.onEditor(event, false), { signal: this.abort.signal });

    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      if (this.inView) this.request();
    });
    this.observer.observe(this);

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.box);

    this.setup();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  setup() {
    const active = !reducedMotion.matches;
    this.toggleAttribute('data-ready', active);
    if (!active) {
      this.style.removeProperty('--sr-p');
      return;
    }
    this.measure();
  }

  // The pinned stage is one screen tall: text that needs more room reads in normal flow.
  measure() {
    const stage = getComputedStyle(this.stage);
    const available = parseFloat(stage.minHeight) || window.innerHeight;
    const needed = this.box.offsetHeight + parseFloat(stage.paddingTop) + parseFloat(stage.paddingBottom);
    this.toggleAttribute('data-static', needed > available + 1);
    this.pinTop = parseFloat(stage.top) || 0;
    this.request();
  }

  update() {
    this.frame = 0;
    if (!this.hasAttribute('data-ready')) return;
    if (this.editing || this.hasAttribute('data-static')) {
      this.write(1);
      return;
    }
    if (!this.inView) return;

    const rect = this.getBoundingClientRect();
    const travel = rect.height - this.stage.offsetHeight;
    const progress = travel > 0 ? clamp((this.pinTop - rect.top) / travel) : 1;
    const end = Number(this.dataset.revealEnd) || 1;
    this.write(clamp(progress / end));
  }

  write(progress) {
    this.style.setProperty('--sr-p', progress.toFixed(4));
  }

  // Selecting the section in the editor shows the final state, so the merchant can read it.
  onEditor(event, selected) {
    if (!event.target.contains(this)) return;
    this.editing = selected;
    this.request();
  }
}

if (!customElements.get('mw-scroll-reading')) customElements.define('mw-scroll-reading', MwScrollReading);
